import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import moment from 'jalali-moment'
import {
    ManagementAddNewLoan,
    ManagementAddNewLoanValidateJustAdd,
    ManagementAddNewLoanValidateFuckingAdd,
} from 'services/loans/manager'
import { getOneMonthLater } from 'utilities/helper/getOneMonthLater'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { loanInterestFields } from 'utilities/helper/loanInterest'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import { ITerms } from './steps/StepTerms'

export interface IGrantDraft {
    moneypoolId?: string
    fundName?: string
    borrower?: ILoanCandidate
    amount: string
    terms: ITerms
    guarantors: IGuarantorOption[]
    note: string
    card?: IBankAccountCards
    /** the Bale nudge this grant started from, if any — closes the funnel */
    nudgeId?: string | null
}

/**
 * Turning the wizard's answers into a loan.
 *
 * Two server calls, always in this order:
 *
 *  1. `draft-installments` asks the backend to build the schedule. The dates
 *     are Jalali-aware (month lengths differ, and a due date can land on a
 *     day the next month does not have), so the client must not invent them.
 *  2. the loan itself, carrying that exact schedule back.
 *
 * The old flow put a screen between the two so the manager could edit
 * individual instalments. That screen still exists and is still reachable —
 * it is just no longer compulsory, because on the overwhelming majority of
 * loans the generated schedule is the one that gets used.
 *
 * `loan_demand` is threaded through whenever the borrower had an open
 * request: that is what closes the request as accepted instead of leaving it
 * sitting in the queue next to the loan that answered it.
 */
export default function useGrantSubmit(
    draft: IGrantDraft,
    /** drop the saved wizard answers once the loan really exists */
    onGranted?: () => void
) {
    const finishFlow = useFinishFlow(FLOWS.grantLoan)
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const buildBase = () => {
        const amount = draft.amount
        const feeValue = Number(draft.terms.fee || 0)
        return {
            amount,
            // «lip_ma» is also what a zero-fee loan uses: there is no fee to
            // place, and the backend requires a mode either way
            interest_pay_mode: feeValue > 0 ? draft.terms.feeMode : 'lip_ma',
            ...loanInterestFields(draft.terms.feeType, feeValue),
            number_of_repayments: draft.terms.installments,
            pay_date: moment().format('YYYY-MM-DD'),
            due_date: draft.terms.startDate || getOneMonthLater(),
            moneyPool: draft.moneypoolId,
            poolShip: String(draft.borrower?.poolship_id ?? ''),
            tag: draft.note,
            loan_demand: draft.borrower?.demand?.id ?? null,
            guarantor_ids: draft.guarantors.map((item) => item.poolship_id),
        }
    }

    const submit = async (recordOnly: boolean) => {
        if (isSubmitting || !draft.borrower || !draft.moneypoolId) return
        setIsSubmitting(true)
        posthog.capture('loan_grant_submit_initiated', {
            fund_id: draft.moneypoolId,
            nudge_id: draft.nudgeId ?? null,
            record_only: recordOnly,
            amount: Number(draft.amount || 0),
            installments: Number(draft.terms.installments || 0),
            guarantors: draft.guarantors.length,
            has_card: !!draft.card,
        })
        const base = buildBase()
        // which of the two calls we are inside, so a failure names the one that
        // broke: a schedule the backend refused to build is a different bug
        // from a loan it refused to create off a schedule it just built
        let stage: 'draft_installments' | 'finalize' = 'draft_installments'
        try {
            const drafted = await ManagementAddNewLoan(base as any)
            const installments = drafted.installments
            stage = 'finalize'

            if (recordOnly) {
                await ManagementAddNewLoanValidateJustAdd({
                    ...(base as any),
                    installments,
                })
            } else {
                await ManagementAddNewLoanValidateFuckingAdd({
                    ...(base as any),
                    installments,
                    bankAccount: draft.card?.id,
                    ctx_id: Number(draft.moneypoolId),
                    ctx_type: 'mnp',
                    destination: 'mba',
                    is_group_pay: false,
                    payer_name: '',
                    receiver: draft.card?.member,
                    receiver_name: '',
                    source: 'hbb',
                    state: '',
                    state_time: '',
                    transaction_code: '',
                } as any)
            }

            posthog.capture('loan_granted', {
                // both this screen and the form it replaces emit `loan_granted`;
                // without this the two are indistinguishable and neither can be
                // measured against the other
                flow: 'wizard',
                record_only: recordOnly,
                fund_id: draft.moneypoolId,
                poolship_id: draft.borrower.poolship_id,
                amount: Number(draft.amount || 0),
                installments: Number(draft.terms.installments || 0),
                from_demand: !!draft.borrower.demand,
                score: draft.borrower.score,
                rank: draft.borrower.rank,
                guarantors: draft.guarantors.length,
                // The last step of the Bale nudge funnel. With this, «sent»
                // (server-side `loan_nudge_sent`), «opened»
                // (`loan_grant_started`, entry_source: bale_nudge) and
                // «granted» are three events on ONE person carrying ONE
                // nudge_id — so "did the notification lead to a loan?" is a
                // single PostHog funnel rather than a join against our tables.
                nudge_id: draft.nudgeId ?? null,
            })
            toast.success('وام با موفقیت ثبت شد')
            // only here: a draft cleared on failure would lose everything the
            // manager typed at the exact moment they need to retry it
            onGranted?.()
            // the fund's balance, the request queue and every member list the
            // score is derived from all moved
            queryClient.invalidateQueries()
            // out of the wizard for good: back from the fund must not land on
            // a step whose draft was just cleared
            finishFlow(`/fund/${draft.moneypoolId}`, {
                poolShipId: draft.borrower.poolship_id,
            })
        } catch (error: any) {
            posthog.capture('loan_grant_failed', {
                flow: 'wizard',
                step: stage,
                record_only: recordOnly,
                fund_id: draft.moneypoolId,
                status: error?.response?.status,
            })
            const detail = error?.response?.data
            if (
                Array.isArray(detail) &&
                detail[0] === 'Interest cannot be more than 4%.'
            ) {
                toast.error('میزان کارمزد نباید بیش از ۴ درصد مبلغ وام باشد')
            } else {
                toast.error('ثبت وام انجام نشد. دوباره تلاش کنید.')
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return { submit, isSubmitting }
}
